import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function NewsletterAdmin() {
    const { authFetch } = useAuth();
    const [activeTab, setActiveTab] = useState('subscribers');
    const [subscribers, setSubscribers] = useState([]);
    const [newsletters, setNewsletters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // New subscriber form
    const [newEmail, setNewEmail] = useState('');
    const [newName, setNewName] = useState('');

    // Newsletter composer
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [newsletterFile, setNewsletterFile] = useState(null);
    const [newsletterType, setNewsletterType] = useState('file'); // 'file' or 'text'
    const [deliveryMode, setDeliveryMode] = useState('inline'); // 'inline' or 'attachment' (HTML only)
    const [emailType, setEmailType] = useState('newsletter'); // 'newsletter' or 'event'
    const [editingNewsletter, setEditingNewsletter] = useState(null);
    const [sending, setSending] = useState(false);

    // Hilfsfunktionen zur Dateityp-Erkennung
    const isHtmlName = (name) => !!name && /\.(html?|xhtml)$/i.test(name);
    const isPdfName = (name) => !!name && /\.pdf$/i.test(name);
    const selectedFileIsHtml = newsletterFile
        ? isHtmlName(newsletterFile.name) || newsletterFile.type === 'text/html'
        : (editingNewsletter && editingNewsletter.html_filename ? true : false);
    const selectedFileIsPdf = newsletterFile
        ? isPdfName(newsletterFile.name) || newsletterFile.type === 'application/pdf'
        : (editingNewsletter && editingNewsletter.pdf_filename ? true : false);
    const existingFileLabel = editingNewsletter
        ? (editingNewsletter.html_filename || editingNewsletter.pdf_filename || '')
        : '';

    // Excluded subscribers (won't receive newsletter)
    const [excludedSubscribers, setExcludedSubscribers] = useState(new Set());

    useEffect(() => {
        if (activeTab === 'subscribers') {
            loadSubscribers();
        } else if (activeTab === 'history') {
            loadNewsletters();
        }
    }, [activeTab]);

    const loadSubscribers = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/newsletter/subscribers');
            const data = await res.json();
            if (data.message === 'success') {
                setSubscribers(data.data);
            }
        } catch (err) {
            setError('Fehler beim Laden der Abonnenten');
        }
        setLoading(false);
    };

    const loadNewsletters = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/newsletters');
            const data = await res.json();
            if (data.message === 'success') {
                setNewsletters(data.data);
            }
        } catch (err) {
            setError('Fehler beim Laden der Newsletter');
        }
        setLoading(false);
    };

    const addSubscriber = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const res = await authFetch('/api/newsletter/subscribers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: newEmail, name: newName })
            });
            const data = await res.json();

            if (res.ok) {
                setSuccess('Abonnent hinzugefuegt');
                setNewEmail('');
                setNewName('');
                loadSubscribers();
            } else {
                setError(data.error || 'Fehler beim Hinzufuegen');
            }
        } catch (err) {
            setError('Fehler beim Hinzufuegen');
        }
    };

    const deleteSubscriber = async (id, source) => {
        if (source === 'member') {
            alert('Mitglieder koennen nicht hier geloescht werden. Bitte im Mitgliederbereich den Newsletter-Consent deaktivieren.');
            return;
        }

        if (!window.confirm('Abonnent wirklich loeschen?')) return;

        try {
            const res = await authFetch(`/api/newsletter/subscribers/${id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                setSuccess('Abonnent geloescht');
                loadSubscribers();
            } else {
                setError('Fehler beim Loeschen');
            }
        } catch (err) {
            setError('Fehler beim Loeschen');
        }
    };

    const saveNewsletter = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            if (newsletterType === 'file') {
                // PDF / HTML Upload
                const hasExistingFile = editingNewsletter?.pdf_filename || editingNewsletter?.html_filename;
                if (!newsletterFile && !hasExistingFile) {
                    setError('Bitte eine PDF- oder HTML-Datei auswaehlen');
                    return;
                }

                const formData = new FormData();
                formData.append('subject', subject);
                if (newsletterFile) {
                    formData.append('file', newsletterFile);
                }
                if (content) {
                    formData.append('content', content);
                }
                formData.append('deliveryMode', deliveryMode);
                if (editingNewsletter) {
                    formData.append('newsletterId', editingNewsletter.id);
                }

                const token = localStorage.getItem('authToken');
                const res = await fetch('/api/newsletters/upload', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });
                const data = await res.json();

                if (res.ok) {
                    setSuccess(editingNewsletter ? 'Newsletter aktualisiert' : 'Newsletter gespeichert');
                    setEditingNewsletter(data.data);
                } else {
                    setError(data.error || 'Fehler beim Speichern');
                }
            } else {
                // Text Newsletter
                const method = editingNewsletter ? 'PUT' : 'POST';
                const url = editingNewsletter
                    ? `/api/newsletters/${editingNewsletter.id}`
                    : '/api/newsletters';

                const res = await authFetch(url, {
                    method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ subject, content })
                });
                const data = await res.json();

                if (res.ok) {
                    setSuccess(editingNewsletter ? 'Newsletter aktualisiert' : 'Entwurf gespeichert');
                    setEditingNewsletter(data.data);
                } else {
                    setError(data.error || 'Fehler beim Speichern');
                }
            }
        } catch (err) {
            setError('Fehler beim Speichern');
        }
    };

    const sendNewsletter = async () => {
        if (!editingNewsletter) {
            setError('Bitte zuerst speichern');
            return;
        }

        const confirmedCount = subscribers.filter(s => s.confirmed).length;
        if (!window.confirm(`Newsletter an ${confirmedCount} Empfaenger senden?`)) return;

        setSending(true);
        setError('');
        setSuccess('');

        try {
            const res = await authFetch(`/api/newsletters/${editingNewsletter.id}/send`, {
                method: 'POST'
            });
            const data = await res.json();

            if (res.ok) {
                const skippedInfo = data.data.skipped > 0 ? ` (${data.data.skipped} ausgeschlossen)` : '';
                setSuccess(`Newsletter an ${data.data.sent} Empfaenger gesendet!${skippedInfo}`);
                resetComposer();
                setExcludedSubscribers(new Set());
                if (data.data.errors && data.data.errors.length > 0) {
                    setError(`${data.data.errors.length} Fehler: ${data.data.errors.map(e => e.email).join(', ')}`);
                }
            } else {
                setError(data.error || 'Fehler beim Senden');
            }
        } catch (err) {
            setError('Fehler beim Senden');
        }
        setSending(false);
    };

    const downloadPdf = (id) => {
        const token = localStorage.getItem('authToken');
        window.open(`/api/newsletters/${id}/pdf?token=${token}`, '_blank');
    };

    const previewHtml = (id) => {
        const token = localStorage.getItem('authToken');
        window.open(`/api/newsletters/${id}/preview-html?token=${token}`, '_blank');
    };

    const editNewsletter = (newsletter) => {
        setSubject(newsletter.subject);
        setContent(newsletter.content || '');
        setNewsletterFile(null);
        const hasFile = newsletter.pdf_filename || newsletter.html_filename;
        setNewsletterType(hasFile ? 'file' : 'text');
        setDeliveryMode(newsletter.delivery_mode === 'inline' ? 'inline' : 'attachment');
        setEditingNewsletter(newsletter);
        setActiveTab('compose');
    };

    const deleteNewsletter = async (id) => {
        if (!window.confirm('Newsletter wirklich loeschen?')) return;

        try {
            const res = await authFetch(`/api/newsletters/${id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                setSuccess('Newsletter geloescht');
                loadNewsletters();
            } else {
                setError('Fehler beim Loeschen');
            }
        } catch (err) {
            setError('Fehler beim Loeschen');
        }
    };

    const resetComposer = () => {
        setSubject('');
        setContent('');
        setNewsletterFile(null);
        setNewsletterType('file');
        setDeliveryMode('inline');
        setEditingNewsletter(null);
    };

    const newDraft = () => {
        resetComposer();
        setActiveTab('compose');
    };

    // Toggle subscriber exclusion
    const toggleSubscriberExclusion = (subscriberKey) => {
        setExcludedSubscribers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(subscriberKey)) {
                newSet.delete(subscriberKey);
            } else {
                newSet.add(subscriberKey);
            }
            return newSet;
        });
    };

    // Select/Deselect all
    const selectAllSubscribers = () => {
        setExcludedSubscribers(new Set());
    };

    const deselectAllSubscribers = () => {
        const allKeys = subscribers
            .filter(s => s.confirmed)
            .map(s => `${s.source}-${s.id}`);
        setExcludedSubscribers(new Set(allKeys));
    };

    // Get active (non-excluded) subscriber count
    const getActiveSubscriberCount = () => {
        return subscribers.filter(s => s.confirmed && !excludedSubscribers.has(`${s.source}-${s.id}`)).length;
    };

    const renderSubscribers = () => (
        <div>
            <h4>Abonnenten verwalten</h4>

            {/* Add subscriber form */}
            <div className="card mb-4">
                <div className="card-header">Neuen Abonnenten hinzufuegen</div>
                <div className="card-body">
                    <form onSubmit={addSubscriber} className="row g-2">
                        <div className="col-md-4">
                            <input
                                type="email"
                                className="form-control"
                                placeholder="E-Mail *"
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Name (optional)"
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <button type="submit" className="btn btn-primary w-100">
                                Hinzufuegen
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Subscriber list */}
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>
                        Alle Abonnenten ({subscribers.filter(s => s.confirmed).length} bestaetigt)
                        {excludedSubscribers.size > 0 && (
                            <span className="text-warning ms-2">
                                ({excludedSubscribers.size} ausgeschlossen)
                            </span>
                        )}
                    </span>
                    <div className="btn-group btn-group-sm">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={selectAllSubscribers}
                        >
                            Alle auswaehlen
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={deselectAllSubscribers}
                        >
                            Keine auswaehlen
                        </button>
                    </div>
                </div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="p-3 text-center">Laden...</div>
                    ) : (
                        <table className="table table-striped mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <span title="Newsletter erhalten">Senden</span>
                                    </th>
                                    <th>E-Mail</th>
                                    <th>Name</th>
                                    <th>Quelle</th>
                                    <th>Status</th>
                                    <th>Angemeldet am</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscribers.map(sub => {
                                    const subscriberKey = `${sub.source}-${sub.id}`;
                                    const isExcluded = excludedSubscribers.has(subscriberKey);
                                    return (
                                        <tr key={subscriberKey} className={isExcluded ? 'table-secondary' : ''}>
                                            <td>
                                                {sub.confirmed && (
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={!isExcluded}
                                                        onChange={() => toggleSubscriberExclusion(subscriberKey)}
                                                        title={isExcluded ? 'Klicken um einzuschliessen' : 'Klicken um auszuschliessen'}
                                                    />
                                                )}
                                            </td>
                                            <td className={isExcluded ? 'text-muted' : ''}>{sub.email}</td>
                                            <td className={isExcluded ? 'text-muted' : ''}>{sub.name || '-'}</td>
                                            <td>
                                                <span className={`badge ${sub.source === 'member' ? 'bg-info' : 'bg-secondary'}`}>
                                                    {sub.source === 'member' ? 'Mitglied' : 'Extern'}
                                                </span>
                                            </td>
                                            <td>
                                                {sub.confirmed ? (
                                                    <span className="badge bg-success">Bestaetigt</span>
                                                ) : (
                                                    <span className="badge bg-warning">Ausstehend</span>
                                                )}
                                            </td>
                                            <td>
                                                {new Date(sub.created_at).toLocaleDateString('de-DE')}
                                            </td>
                                            <td>
                                                {sub.source === 'external' && (
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => deleteSubscriber(sub.id, sub.source)}
                                                    >
                                                        Loeschen
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {subscribers.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            Keine Abonnenten vorhanden
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );

    const saveAndSend = async () => {
        if (!subject) {
            setError('Bitte einen Betreff eingeben');
            return;
        }

        const hasExistingFile = editingNewsletter?.pdf_filename || editingNewsletter?.html_filename;
        if (newsletterType === 'file' && !newsletterFile && !hasExistingFile) {
            setError('Bitte eine PDF- oder HTML-Datei auswaehlen');
            return;
        }

        if (newsletterType === 'text' && !content) {
            setError('Bitte einen Inhalt eingeben');
            return;
        }

        const activeCount = getActiveSubscriberCount();
        if (activeCount === 0) {
            setError('Keine Empfaenger ausgewaehlt');
            return;
        }

        const typeLabel = emailType === 'event' ? 'Event-E-Mail' : 'Newsletter';
        if (!window.confirm(`${typeLabel} an ${activeCount} Empfaenger senden?`)) return;

        setSending(true);
        setError('');
        setSuccess('');

        try {
            let newsletterId = editingNewsletter?.id;

            // Beim Bearbeiten ggf. neue Datei oder geaenderten Modus speichern
            if (editingNewsletter && newsletterType === 'file') {
                if (newsletterFile) {
                    const formData = new FormData();
                    formData.append('subject', subject);
                    formData.append('file', newsletterFile);
                    if (content) formData.append('content', content);
                    formData.append('deliveryMode', deliveryMode);
                    formData.append('newsletterId', editingNewsletter.id);

                    const token = localStorage.getItem('authToken');
                    const saveRes = await fetch('/api/newsletters/upload', {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: formData
                    });
                    const saveData = await saveRes.json();
                    if (!saveRes.ok) {
                        setError(saveData.error || 'Fehler beim Speichern');
                        setSending(false);
                        return;
                    }
                } else if (editingNewsletter.html_filename && editingNewsletter.delivery_mode !== deliveryMode) {
                    // Nur Mode geaendert - separat aktualisieren
                    const modeRes = await authFetch(`/api/newsletters/${editingNewsletter.id}/delivery-mode`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ deliveryMode })
                    });
                    if (!modeRes.ok) {
                        const modeData = await modeRes.json();
                        setError(modeData.error || 'Fehler beim Speichern des Versandmodus');
                        setSending(false);
                        return;
                    }
                }
            }

            // Erst speichern falls noch nicht gespeichert
            if (!newsletterId) {
                if (newsletterType === 'file') {
                    const formData = new FormData();
                    formData.append('subject', subject);
                    formData.append('file', newsletterFile);
                    if (content) {
                        formData.append('content', content);
                    }
                    formData.append('deliveryMode', deliveryMode);

                    const token = localStorage.getItem('authToken');
                    const saveRes = await fetch('/api/newsletters/upload', {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: formData
                    });
                    const saveData = await saveRes.json();

                    if (!saveRes.ok) {
                        setError(saveData.error || 'Fehler beim Speichern');
                        setSending(false);
                        return;
                    }
                    newsletterId = saveData.data.id;
                } else {
                    const saveRes = await authFetch('/api/newsletters', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ subject, content })
                    });
                    const saveData = await saveRes.json();

                    if (!saveRes.ok) {
                        setError(saveData.error || 'Fehler beim Speichern');
                        setSending(false);
                        return;
                    }
                    newsletterId = saveData.data.id;
                }
            }

            // Ausgeschlossene E-Mails sammeln
            const excludedEmails = subscribers
                .filter(s => s.confirmed && excludedSubscribers.has(`${s.source}-${s.id}`))
                .map(s => s.email);

            // Dann senden
            const res = await authFetch(`/api/newsletters/${newsletterId}/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ excludedEmails, emailType })
            });
            const data = await res.json();

            if (res.ok) {
                const skippedInfo = data.data.skipped > 0 ? ` (${data.data.skipped} ausgeschlossen)` : '';
                setSuccess(`Newsletter an ${data.data.sent} Empfaenger gesendet!${skippedInfo}`);
                resetComposer();
                setExcludedSubscribers(new Set());
                if (data.data.errors && data.data.errors.length > 0) {
                    setError(`${data.data.errors.length} Fehler: ${data.data.errors.map(e => e.email).join(', ')}`);
                }
            } else {
                setError(data.error || 'Fehler beim Senden');
            }
        } catch (err) {
            setError('Fehler beim Senden');
        }
        setSending(false);
    };

    const previewExistingHtml = () => {
        if (!editingNewsletter) return;
        const token = localStorage.getItem('authToken');
        window.open(`/api/newsletters/${editingNewsletter.id}/preview-html?token=${token}`, '_blank');
    };

    const isHtmlMode = selectedFileIsHtml;
    const fileButtonDisabled = sending
        || !subject
        || (!newsletterFile && !(editingNewsletter && (editingNewsletter.pdf_filename || editingNewsletter.html_filename)))
        || getActiveSubscriberCount() === 0;

    const renderComposer = () => (
        <div>
            <h4>Newsletter versenden</h4>

            <div className="mb-3">
                <label className="form-label">E-Mail-Typ *</label>
                <select
                    className="form-select"
                    value={emailType}
                    onChange={e => setEmailType(e.target.value)}
                >
                    <option value="newsletter">Newsletter</option>
                    <option value="event">Event / Veranstaltung</option>
                </select>
                <small className="text-muted">
                    Bestimmt Überschrift und Texte im E-Mail-Template (z.B. "FutuRhinos Event" statt "FutuRhinos Newsletter").
                </small>
            </div>

            <div className="mb-3">
                <label className="form-label">Betreff / Titel *</label>
                <input
                    type="text"
                    className="form-control"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder={emailType === 'event' ? 'z.B. Einladung zum FutuRhinos Sommerfest' : 'z.B. FutuRhinos Newsletter - April 2026'}
                />
            </div>

            <div className="mb-3">
                <label className="form-label">Newsletter-Datei (PDF oder HTML) *</label>
                <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.html,.htm,application/pdf,text/html"
                    onChange={e => setNewsletterFile(e.target.files[0] || null)}
                />
                {newsletterFile ? (
                    <small className="text-success d-block mt-1">
                        Ausgewaehlt: {newsletterFile.name}
                        {selectedFileIsHtml && <span className="badge bg-info ms-2">HTML</span>}
                        {selectedFileIsPdf && <span className="badge bg-secondary ms-2">PDF</span>}
                    </small>
                ) : existingFileLabel ? (
                    <small className="text-muted d-block mt-1">
                        Aktuell gespeichert: {existingFileLabel}
                        {editingNewsletter?.html_filename && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ms-2"
                                onClick={previewExistingHtml}
                            >
                                Vorschau ansehen
                            </button>
                        )}
                    </small>
                ) : null}
                <small className="text-muted d-block">
                    PDFs werden immer als Anhang gesendet. HTML-Dateien koennen direkt als Vorschau in der E-Mail eingebettet werden.
                </small>
            </div>

            {isHtmlMode && (
                <div className="mb-3">
                    <label className="form-label">Versandart fuer HTML-Newsletter *</label>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="radio"
                            id="deliveryInline"
                            name="deliveryMode"
                            value="inline"
                            checked={deliveryMode === 'inline'}
                            onChange={() => setDeliveryMode('inline')}
                        />
                        <label className="form-check-label" htmlFor="deliveryInline">
                            <strong>Als Vorschau in der E-Mail (empfohlen)</strong>
                            <br />
                            <small className="text-muted">
                                Der Newsletter wird direkt im E-Mail-Body angezeigt - die Empfaenger sehen den kompletten Newsletter ohne Anhang oeffnen zu muessen.
                            </small>
                        </label>
                    </div>
                    <div className="form-check mt-2">
                        <input
                            className="form-check-input"
                            type="radio"
                            id="deliveryAttachment"
                            name="deliveryMode"
                            value="attachment"
                            checked={deliveryMode === 'attachment'}
                            onChange={() => setDeliveryMode('attachment')}
                        />
                        <label className="form-check-label" htmlFor="deliveryAttachment">
                            <strong>Als HTML-Anhang</strong>
                            <br />
                            <small className="text-muted">
                                Die E-Mail enthaelt nur einen kurzen Hinweis und die HTML-Datei als Anhang.
                            </small>
                        </label>
                    </div>
                </div>
            )}

            <div className="mb-4">
                <label className="form-label">Begleittext (optional)</label>
                <textarea
                    className="form-control"
                    rows="4"
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    placeholder={isHtmlMode && deliveryMode === 'inline'
                        ? 'Optionaler Text, der oberhalb des Newsletters in der E-Mail erscheint...'
                        : 'Optionaler Text, der in der E-Mail vor dem Anhang-Hinweis erscheint...'}
                />
                <small className="text-muted">
                    {isHtmlMode && deliveryMode === 'inline'
                        ? 'Optionaler Vortext - wird vor dem eingebetteten Newsletter angezeigt.'
                        : 'Dieser Text erscheint in der E-Mail. Die Datei wird als Anhang mitgesendet.'}
                </small>
            </div>

            <div className={`alert ${excludedSubscribers.size > 0 ? 'alert-warning' : 'alert-info'}`}>
                <strong>{getActiveSubscriberCount()} von {subscribers.filter(s => s.confirmed).length} Empfaengern</strong> werden diesen Newsletter erhalten.
                {excludedSubscribers.size > 0 && (
                    <span> ({excludedSubscribers.size} ausgeschlossen)</span>
                )}
                <br />
                <small>
                    <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('subscribers'); }}>
                        Empfaenger verwalten
                    </a>
                </small>
            </div>

            <div className="d-flex gap-2">
                <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    onClick={saveAndSend}
                    disabled={fileButtonDisabled}
                >
                    {sending ? 'Wird gesendet...' : `${emailType === 'event' ? 'Event-E-Mail' : 'Newsletter'} jetzt senden (${getActiveSubscriberCount()})`}
                </button>

                {(subject || newsletterFile || content) && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={resetComposer}
                    >
                        Zuruecksetzen
                    </button>
                )}
            </div>
        </div>
    );

    const renderHistory = () => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Newsletter-Historie</h4>
                <button className="btn btn-primary" onClick={newDraft}>
                    Neuer Newsletter
                </button>
            </div>

            {loading ? (
                <div className="text-center">Laden...</div>
            ) : (
                <div className="list-group">
                    {newsletters.map(nl => (
                        <div key={nl.id} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h5 className="mb-1">
                                        {nl.subject}
                                        {nl.pdf_filename && (
                                            <span className="badge bg-secondary ms-2">PDF</span>
                                        )}
                                        {nl.html_filename && (
                                            <span className="badge bg-info ms-2">
                                                HTML {nl.delivery_mode === 'inline' ? '(Vorschau)' : '(Anhang)'}
                                            </span>
                                        )}
                                    </h5>
                                    {nl.content && (
                                        <p className="mb-1 text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                                            {nl.content.length > 200 ? nl.content.substring(0, 200) + '...' : nl.content}
                                        </p>
                                    )}
                                    {(nl.pdf_filename || nl.html_filename) && (
                                        <p className="mb-1 text-muted">
                                            Datei: {nl.pdf_filename || nl.html_filename}
                                        </p>
                                    )}
                                    <small className="text-muted">
                                        Erstellt: {new Date(nl.created_at).toLocaleDateString('de-DE')}
                                        {nl.sent_at && (
                                            <span className="ms-3">
                                                Gesendet: {new Date(nl.sent_at).toLocaleDateString('de-DE')} an {nl.recipient_count} Empfaenger
                                            </span>
                                        )}
                                    </small>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    {nl.sent_at ? (
                                        <span className="badge bg-success">Gesendet</span>
                                    ) : (
                                        <span className="badge bg-secondary">Entwurf</span>
                                    )}
                                    {!nl.pdf_filename && !nl.html_filename && (
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => downloadPdf(nl.id)}
                                        >
                                            PDF
                                        </button>
                                    )}
                                    {nl.html_filename && (
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => previewHtml(nl.id)}
                                        >
                                            Vorschau
                                        </button>
                                    )}
                                    {!nl.sent_at && (
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => editNewsletter(nl)}
                                        >
                                            Bearbeiten
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => deleteNewsletter(nl.id)}
                                    >
                                        Loeschen
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {newsletters.length === 0 && (
                        <div className="list-group-item text-center text-muted">
                            Noch keine Newsletter vorhanden
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div>
            <h3 className="mb-4">Newsletter</h3>

            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'subscribers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('subscribers')}
                    >
                        Abonnenten
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'compose' ? 'active' : ''}`}
                        onClick={() => setActiveTab('compose')}
                    >
                        Newsletter erstellen
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        Historie
                    </button>
                </li>
            </ul>

            {/* Tab Content */}
            {activeTab === 'subscribers' && renderSubscribers()}
            {activeTab === 'compose' && renderComposer()}
            {activeTab === 'history' && renderHistory()}
        </div>
    );
}

export default NewsletterAdmin;
